import React from 'react';
import { Box, Checkbox, IconButton, TextField, MenuItem, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { ReferralData, DeduplicationMatch } from '../types';
import { STATUS } from '../../LeadManagement/constants';

export interface ColumnConfig {
  id: string;
  label: string;
  accessor: keyof ReferralData | string;
  sortable?: boolean;
  width?: string;
  maxWidth?: string;
  align?: 'left' | 'right' | 'center';
  dropDown?: {
    label: string;
    value: string;
  }[];
}

interface RenderCellValueProps {
  column: ColumnConfig;
  row: ReferralData;
  rowOpen: boolean;
  setRowOpen: (open: boolean) => void;
  enableExpandableRows: boolean;
  onSelectRow: (id: string) => void;
  isCheckboxDisabled: boolean;
  editedCCId: string;
  setEditedCCId: (value: string) => void;
  statusValue: string;
  setStatusValue: (value: string) => void;
  ccIdError: string;
  setCcIdError: (error: string) => void;
  validateCCId: (ccId: string, status: string) => string;
  onPatientCCIdChange?: (id: string, newValue: string) => void;
  onStatusChange?: (id: string, newValue: string) => void;
  currentUser: string;
  lockedRecordsCount: number;
  onLockToggle?: (id: string) => void;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  hideSendToCCButton?: boolean;
}

export const renderCellValue = (props: RenderCellValueProps): React.ReactNode => {
  const {
    column,
    row,
    rowOpen,
    setRowOpen,
    enableExpandableRows,
    onSelectRow,
    isCheckboxDisabled,
    // editedCCId,
    setEditedCCId,
    statusValue,
    setStatusValue,
    // ccIdError,
    setCcIdError,
    // validateCCId,
    onPatientCCIdChange,
    onStatusChange,
    currentUser,
    lockedRecordsCount,
    onLockToggle,
    hideSendToCCButton,
  } = props;

  if (column.id === 'expand') {
    const hasExpandableData = true;

    return enableExpandableRows && hasExpandableData ? (
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setRowOpen(!rowOpen);
        }}
      >
        {rowOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
    ) : null;
  }

  if (column.id === 'lock') {
    const isLockedByCurrentUser = row.lockedBy === currentUser;
    const isLockedByOther = row.lockedBy && row.lockedBy !== currentUser;
    const canLock = !row.lockedBy && lockedRecordsCount < 5;
    const canUnlock = isLockedByCurrentUser;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (onLockToggle && (canLock || canUnlock)) {
              onLockToggle(row.id);
            }
          }}
          disabled={isLockedByOther || (!canLock && !canUnlock)}
          sx={{
            color: row.lockedBy
              ? isLockedByCurrentUser
                ? (theme: any) => theme.palette.success?.main || '#4caf50'
                : (theme: any) => theme.palette.error?.main || '#f44336'
              : (theme: any) => theme.palette.neutral6?.main || '#666',
            '&.Mui-disabled': {
              color: (theme: any) => theme.palette.error?.main || '#f44336',
            },
          }}
          title={
            isLockedByOther
              ? `Locked by ${row.lockedBy}`
              : isLockedByCurrentUser
              ? 'Click to unlock (You have locked this record)'
              : !canLock
              ? 'Maximum 5 records can be locked'
              : 'Click to lock this record'
          }
        >
          {row.lockedBy ? (
            <LockIcon sx={{ fontSize: 20 }} />
          ) : (
            <LockOpenIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>
    );
  }

  if (column.id === 'patientType') {
    const isMinor = row.patientType === 'minor';
    const isReturningUser = row.dedupeStatus === 'Returning-User';

    // Show icons based on conditions
    if (isMinor || isReturningUser) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {isMinor && (
            <Tooltip title="Minor Patient" arrow placement="top">
              <EscalatorWarningIcon
                sx={{
                  fontSize: 20,
                  color: '#3F4040',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          {isReturningUser && (
            <Tooltip title="Returning Patient" arrow placement="top">
              <SettingsBackupRestoreIcon
                sx={{
                  fontSize: 20,
                  color: '#3F4040',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    }
    return null;
  }

  if (column.id === 'select') {
    // Hide checkbox for auto-merge status records
    if (row.status === STATUS.AUTO_MERGE) {
      return null;
    }

    return (
      <Checkbox
        checked={row.selected}
        onChange={() => onSelectRow(row.id)}
        onClick={(e) => e.stopPropagation()}
        disabled={isCheckboxDisabled}
        sx={{
          color: (theme: any) => theme.palette.neutral5?.main,
          '&.Mui-checked': {
            color: (theme: any) => theme.palette.themeColor?.main,
          },
          '&.Mui-disabled': {
            color: (theme: any) => theme.palette.neutral4?.main,
          },
        }}
      />
    );
  }

  if ((column.dropDown?.length || 0) > 0) {
    const options = column.dropDown;
    const isLockedByOther = !!(row.lockedBy && row.lockedBy !== currentUser);
    return (
      <TextField
        select
        value={statusValue}
        onChange={(e) => {
          const newValue = e.target.value;
          setStatusValue(newValue);

          // If status changes to "New-Record", clear the CC Patient ID
          if (newValue === STATUS.NEW_RECORD) {
            setEditedCCId('');
            setCcIdError('');
            if (onPatientCCIdChange) {
              onPatientCCIdChange(row.id, '');
            }
            if (onStatusChange) {
              onStatusChange(row.id, newValue);
            }
          } else {
            // Validate CC ID with new status
            // const error = validateCCId(editedCCId, newValue);
            // setCcIdError(error);
            if (onStatusChange) {
              onStatusChange(row.id, newValue);
            }
          }
        }}
        onClick={(e) => e.stopPropagation()}
        size="small"
        fullWidth
        disabled={isLockedByOther || hideSendToCCButton}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '13px',
            '&:hover fieldset': {
              borderColor: (theme: any) => theme.palette.themeColor?.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: (theme: any) => theme.palette.themeColor?.main,
            },
          },
        }}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  // Editable Patient CC ID field
  // if (column.id === 'patientCCId') {
  //   const isLockedByOther = !!(row.lockedBy && row.lockedBy !== currentUser);
  //   const isDisabled =
  //     statusValue === STATUS.NEW_RECORD || statusValue === STATUS.AUTO_MERGE || isLockedByOther;

  //   return (
  //     <Box>
  //       <TextField
  //         value={editedCCId}
  //         onChange={(e) => {
  //           const newValue = e.target.value;
  //           setEditedCCId(newValue);
  //           // Validate on change
  //           const error = validateCCId(newValue, statusValue);
  //           setCcIdError(error);
  //         }}
  //         onBlur={() => {
  //           const error = validateCCId(editedCCId, statusValue);
  //           setCcIdError(error);

  //           if (!error && onPatientCCIdChange && editedCCId !== row.patientCCId) {
  //             onPatientCCIdChange(row.id, editedCCId);
  //           }
  //         }}
  //         onKeyDown={(e) => {
  //           if (e.key === 'Enter') {
  //             const error = validateCCId(editedCCId, statusValue);
  //             setCcIdError(error);

  //             if (!error && onPatientCCIdChange && editedCCId !== row.patientCCId) {
  //               onPatientCCIdChange(row.id, editedCCId);
  //             }
  //           }
  //           if (e.key === 'Escape') {
  //             setEditedCCId(row.patientCCId);
  //             setCcIdError('');
  //           }
  //         }}
  //         onClick={(e) => e.stopPropagation()}
  //         size="small"
  //         disabled={isDisabled}
  //         error={!!ccIdError}
  //         sx={{
  //           '& .MuiOutlinedInput-root': {
  //             fontSize: '13px',
  //             '&:hover fieldset': {
  //               borderColor: (theme: any) =>
  //                 ccIdError ? theme.palette.error?.main : theme.palette.themeColor?.main,
  //             },
  //             '&.Mui-focused fieldset': {
  //               borderColor: (theme: any) =>
  //                 ccIdError ? theme.palette.error?.main : theme.palette.themeColor?.main,
  //             },
  //             '&.Mui-error fieldset': {
  //               borderColor: (theme: any) => theme.palette.error?.main,
  //             },
  //           },
  //         }}
  //       />
  //       {ccIdError && (
  //         <Typography
  //           variant="caption"
  //           sx={{
  //             color: (theme: any) => theme.palette.error?.main,
  //             fontSize: '10px',
  //             display: 'block',
  //             mt: 0.25,
  //           }}
  //         >
  //           {ccIdError}
  //         </Typography>
  //       )}
  //     </Box>
  //   );
  // }

  const value = row[column.accessor as keyof ReferralData];
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return (value as string) || '-';
};
